<template>
    <div>
        <div class="row">
            <div class="col-md-9">
                <h3 class="text-dark">
                    <i class="el-icon-setting"></i>
                    <span>Advanced</span>
                </h3>

                <p>
                    <small>
                        Fine-tune voice behavior and call handling for experienced users. These advanced settings allow you to customize 
                        conversation quality, natural flow, and call management parameters for optimal performance.
                    </small>
                </p>
            
            <span class="page-anchor" id="speech_settings"></span>
            
            <h4 class="mb-4 _500">Speech Settings</h4>
            
            <el-form-item>
                <div class="form-label">
                    <h5 class="text-dark">Responsiveness</h5>
                    <small>Control how fast the agent responds after users finish speaking. Higher values = faster responses but may cut off speakers. Lower values = more patience but slower conversations. <b>Default: 1</b></small>
                </div>
                <el-slider v-model="bot.settings.retellai.responsiveness"
                    :min=0.1
                    :max=1
                    :step=0.01
                    data-testid="aloai-agent-responsiveness-slider"
                />
                <span>Current value: {{ bot.settings.retellai.responsiveness }}</span>
            </el-form-item>
            
            <el-form-item>
                <div class="form-label">
                    <h5 class="text-dark">Interruption Sensitivity</h5>
                    <small>Control how sensitively the agent can be interrupted by human speech. Higher values make the agent easier to interrupt, allowing more natural conversation flow. Lower values prevent unwanted interruptions. <b>Default: 0.8</b></small>
                </div>
                <el-slider v-model="bot.settings.retellai.interruption_sensitivity"
                    :min=0.1
                    :max=1
                    :step=0.1
                    data-testid="aloai-agent-interruption-slider"
                />
                <span>Current value: {{ bot.settings.retellai.interruption_sensitivity }}</span>
            </el-form-item>
            
            <el-form-item v-if="bot.type === AloAi.TYPE_VOICE">
                <div class="form-label">
                    <h5 class="text-dark">Voice Speed</h5>
                    <small>Control how fast the agent speaks. Adjust speaking pace to match your brand voice. Slower speeds convey authority, faster speeds suggest efficiency. <b>Default: 1</b></small>
                </div>
                <el-slider v-model="bot.settings.retellai.voice_speed"
                    :min=0.5
                    :max=2
                    :step=0.01
                />
                <span>Current value: {{ bot.settings.retellai.voice_speed }}</span>
            </el-form-item>
            <el-form-item v-if="bot.type === AloAi.TYPE_VOICE">
                <div class="form-label">
                    <h5 class="text-dark">Voice Temperature</h5>
                    <small>Control how creative the agent is. Lower value means more stable, and higher value means more variant speech generation. <b>Default: 1</b></small>
                </div>
                <el-slider v-model="bot.settings.retellai.voice_temperature"
                    :min=0
                    :max=2
                    :step=0.01
                />
                <span>Current value: {{ bot.settings.retellai.voice_temperature }}</span>
            </el-form-item>
            <el-form-item v-if="bot.type === AloAi.TYPE_VOICE">
                <div class="form-label">
                    <h5 class="text-dark">Voice Volume</h5>
                    <small>Control the volume of the agent's voice. <b>Default: 1</b></small>
                </div>
                <el-slider v-model="bot.settings.retellai.volume"
                    :min=0
                    :max=2
                    :step=0.01
                />
                <span>Current value: {{ bot.settings.retellai.volume }}</span>
            </el-form-item>
            <el-form-item>
                <div class="form-label">
                    <h5 class="text-dark">Back-channeling</h5>
                    <small>The agent will use affirmations like 'yeah' or 'uh-huh' during conversations, indicating active listening and engagement. <b>Default: disabled</b></small>
                </div>
                <el-switch
                    v-model="bot.settings.retellai.enable_backchannel"
                    active-color="#00BF50"
                />
                <small class="font-weight-bold custom-toggle-label ml-2">Enable back-channeling</small>
            </el-form-item>
            <el-form-item
                v-if="!isRealtimeVoiceModel">
                <div class="form-label">
                    <h5 class="text-dark">Fallback voice</h5>
                    <small>If the current voice provider fails, assign a fallback voice to continue the call.</small>
                </div>
                <div class="d-flex align-items-center voice-selector-width">
                    <el-select
                        class="w-100"
                        v-model="bot.settings.retellai.fallback_voice_ids"
                        @change="stopAudio"
                        filterable
                        placeholder="Select voice"
                        :disabled="isRealtimeVoiceModel"
                        v-if="groupedVoicesFallback && groupedVoicesFallback.length > 0"
                    >
                        <el-option
                            :key="null"
                            label="No fallback voice"
                            :value="null"
                        />
                        <el-option-group
                            v-for="group in groupedVoicesFallback"
                            :key="group.label"
                            :label="group.label"
                        >
                            <el-option
                                v-for="voice in group.voices"
                                :key="voice.id"
                                :label="getFormattedVoiceLabel(voice)"
                                :value="voice.id"
                            >
                                <template #default>
                                    <div class="voice-option">
                                        <span class="gender-circle"
                                              :class="getGenderClass(voice.gender)">
                                            {{ getGenderName(voice.gender) }}
                                        </span>
                                        {{ getVoiceName(voice) }}
                                    </div>
                                    <div class="voice-tags mb-1 w-75">
                                        <el-tag
                                            v-if="getLanguage(voice.language)"
                                            size="mini"
                                            class="mr-1 voice-tag"
                                            type="info"
                                        >
                                            {{ getLanguage(voice.language) }}

                                            <span
                                                v-if="getAccent(voice.accent)"
                                                class="accent-tag"
                                            >
                                                ({{ getAccent(voice.accent) }})
                                            </span>
                                        </el-tag>
                                    </div>
                                </template>
                            </el-option>
                        </el-option-group>
                    </el-select>
                    <div v-else class="text-muted">Loading voices...</div>
                    <el-button
                        v-if="bot.settings.retellai.fallback_voice_ids"
                        class="ml-2"
                        circle
                        @click="toggleAudio(bot.settings.retellai.fallback_voice_ids)"
                    >
                        <i :class="isPlaying ? 'fas fa-stop' : 'fas fa-play'"></i>
                    </el-button>
                </div>
            </el-form-item>
            
            <el-form-item>
                <div class="form-label">
                    <h5 class="text-dark">Ambient Sound</h5>
                    <small>Add ambient environment sound to the call to make the experience more realistic.</small>
                </div>
                <div class="d-flex align-items-center voice-selector-width">
                    <el-select
                        class="w-100"
                        v-model="bot.settings.retellai.ambient_sound"
                        placeholder="Select ambient sound"
                        filterable
                    >
                        <el-option
                            :key="null"
                            label="No ambient sound"
                            :value="null"
                        />
                        <el-option
                            v-for="option in AloAi.AMBIENT_SOUND_OPTIONS"
                            :key="option.value"
                            :label="option.label"
                            :value="option.value"
                        />
                    </el-select>
                </div>
            </el-form-item>
            
            <span class="page-anchor" id="call_settings"></span>
            
            <h4 class="mb-4 _500">Call Settings</h4>

            <el-form-item>
                <div class="form-label">
                    <small>
                        Configure call duration limits and behavior patterns to ensure optimal customer experience and resource management.
                    </small>
                </div>
            </el-form-item>
            
            <el-form-item>
                <div class="form-label">
                    <h5 class="text-dark">End Call on Silence</h5>
                    <small>Control how much time the agent should wait for silence before ending the call. Longer durations give customers more time to respond but may waste resources. Shorter durations end calls quickly but might seem abrupt. <b>Default: 1 minute</b></small>
                </div>
                <el-slider v-model="bot.settings.retellai.end_call_after_silence_ms"
                    :format-tooltip="formatTooltip"
                    :min=10000
                    :max=600000
                    :step=1000
                />
                <span>Current value: {{ formatTooltip(bot.settings.retellai.end_call_after_silence_ms) }}</span>
            </el-form-item>
            
            <el-form-item>
                <div class="form-label">
                    <h5 class="text-dark">Max Call Duration</h5>
                    <small>Maximum allowed length for the call, this will force end the call if reached. Prevents extremely long calls that might indicate issues. Consider your typical call duration and add buffer time. <b>Default: 1 hour</b></small>
                </div>
                <el-slider v-model="bot.settings.retellai.max_call_duration_ms"
                    :format-tooltip="formatTooltip"
                    :min=60000
                    :max=7200000
                    :step=1000
                />
                <span>Current value: {{ formatTooltip(bot.settings.retellai.max_call_duration_ms) }}</span>
            </el-form-item>
            <!-- Pause Before Speaking removed: begin_message_delay_ms is now controlled exclusively by Speak First -->
            <el-form-item>
                <div class="form-label">
                    <h5 class="text-dark">Reminder Message Frequency</h5>
                    <small>Control how often agent will send a reminder message. <b>Default: 15 seconds for 3 times</b></small>
                </div>
                <div class="row align-items-center">
                    <div class="col-4">
                        <el-form-item
                            class="mb-0"
                        >
                            <el-slider v-model="bot.settings.retellai.reminder_trigger_ms"
                                :format-tooltip="formatTooltip"
                                :min=1000
                                :max=60000
                                :step=1000
                                size="small"
                            />
                        </el-form-item>
                    </div>
                    <div class="col-2 text-center">
                        {{ formatTooltip(bot.settings.retellai.reminder_trigger_ms) }}
                        for
                    </div>
                    <div class="col-4">
                        <el-form-item
                            class="mb-0"
                        >
                            <el-slider v-model="bot.settings.retellai.reminder_max_count"
                                :min=1
                                :max=15
                                :step=1
                                size="small"
                            />
                        </el-form-item>
                    </div>
                    <div class="col-2 text-left">
                        {{ bot.settings.retellai.reminder_max_count }}
                        times
                    </div>
                </div>
            </el-form-item>
            </div>
            
            <div class="col-md-3 d-none d-md-block">
                <on-this-page
                    :items="[
                        { id: 'speech_settings', label: 'Speech Settings' },
                        { id: 'call_settings', label: 'Call Settings' }
                    ]"
                />
            </div>
        </div>
    </div>
</template>

<script>
import { mapGetters, mapState } from 'vuex'
import * as AloAi from '../../../../constants/aloai'
import audioPlayerMixin from '../../../../mixins/audio-player-mixin'
import voiceUtilsMixin from '../../../../mixins/voice-utils-mixin'
import OnThisPage from '../../../common/on-this-page.vue'

export default {
    name: 'advanced-tab',

    components: {
        OnThisPage
    },

    mixins: [voiceUtilsMixin, audioPlayerMixin],

    props: {
        bot: {
            type: Object,
            required: true
        }
    },

    data() {
        return {
            AloAi
        }
    },

    computed: {
        ...mapState('aloai', ['voices']),
        ...mapGetters('aloai', ['getFilteredVoices', 'getGroupedVoices']),

        isRealtimeVoiceModel() {
            return this.bot.voice_model === AloAi.VOICE_MODEL_GPT_REALTIME_15 ||
                   this.bot.voice_model === AloAi.VOICE_MODEL_GPT_REALTIME_MINI
        },

        voicesList() {
            return this.voices || []
        },

        groupedVoicesFallback() {
            const voices = this.filteredVoices || []
            const currentMainVoice = this.voicesList.find(
                (voice) => voice.id === this.bot.voice_id
            )

            // Initialize groups for ElevenLabs and OpenAI
            const groups = {
                elevenlabs: [],
                openai: []
            }

            if (Array.isArray(voices)) {
                voices.forEach(voice => {
                    if (groups.hasOwnProperty(voice.provider) && voice.provider !== currentMainVoice?.provider) {
                        groups[voice.provider].push(voice)
                    }
                })
            }

            // Return groups in from less to more voices order
            let voice_groups = [
                {label: AloAi.LABEL_ELEVENLABS, voices: groups.elevenlabs},
                {label: AloAi.LABEL_OPENAI, voices: groups.openai},
            ]

            return voice_groups
        },

        filteredVoices() {
            return this.getFilteredVoices(this.bot.voice_model) || []
        }
    },

    methods: {
        formatTooltip(ms) {
            const totalSeconds = Math.floor(ms / 1000)
            const hours = Math.floor(totalSeconds / 3600)
            const minutes = Math.floor((totalSeconds % 3600) / 60)
            const seconds = totalSeconds % 60

            const parts = []
            if (hours) parts.push(`${hours} ${hours === 1 ? 'hour' : 'hours'}`)
            if (minutes) parts.push(`${minutes} ${minutes === 1 ? 'minute' : 'minutes'}`)
            if (seconds || parts.length === 0) parts.push(`${seconds} ${seconds === 1 ? 'second' : 'seconds'}`)

            return parts.join(', ').replace(/,([^,]*)$/, ' and$1')
        }
    }
}
</script>

<style scoped>
.voice-selector-width {
    width: 350px;
}

.voice-option {
    display: flex;
    align-items: center;
}

.gender-circle {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 16px;
    height: 16px;
    border-radius: 50%;
    margin-right: 5px;
    color: white;
    font-size: 10px;
}

.gender-male {
    background-color: rgb(86, 175, 248);
}

.gender-female {
    background-color: rgb(250, 202, 209);
    border: 1px solid pink;
    color: white;
}

.gender-unknown {
    background-color: grey;
}

.voice-tags {
  display: flex;
  flex-wrap: wrap;
}

.voice-tag {
  color: black;
}

.accent-tag {
  margin-left: 1px;
}

.custom-toggle-label {
    margin-top: 2px;
    color: #6d6e6e;
}

</style> 
